import React, { useState, useEffect } from "react";
import { Modal, Button, notification } from "antd";
import { CheckOutlined, RocketOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import companySettingsApi from "modules/client/settings/api/companySettingsApi";
import clientBillingApi from "modules/client/billing/store/api";
import { ENTITLEMENT_IDS } from "configs/entitlements";
import "./AIImportUpsellModal.scss";

const AIImportUpsellModal = ({ visible, onCancel }) => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(true);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        setLoadingPrice(true);
        // Use the same API as UpgradesPage to get entitlements with pricing
        const entitlements = await clientBillingApi.getClientEntitlements();

        // Find the AI Import entitlement by its specific UUID
        const aiImportEntitlement = entitlements.find(
          (ent) => ent.id === ENTITLEMENT_IDS.AI_IMPORT
        );

        if (aiImportEntitlement) {
          console.log("AI Import entitlement found:", {
            id: aiImportEntitlement.id,
            name: aiImportEntitlement.name,
            cost: aiImportEntitlement.cost,
            term: aiImportEntitlement.term
          });
          setPricing(aiImportEntitlement);
        } else {
          console.warn(
            "AI Import entitlement not found in client entitlements. Available entitlements:",
            entitlements.map((e) => ({ id: e.id, name: e.name }))
          );
        }
      } catch (error) {
        console.error("Error fetching AI Import pricing:", error);
      } finally {
        setLoadingPrice(false);
      }
    };

    if (visible) {
      fetchPricing();
    }
  }, [visible]);

  const handleActivate = async () => {
    setLoading(true);
    try {
      // Use the correct API: PUT /api/billing/client-entitlements
      await clientBillingApi.updateClientEntitlement(ENTITLEMENT_IDS.AI_IMPORT, true);

      notification.success({
        message: "AI Import Activated!",
        description: "You now have access to AI-powered route imports.",
        placement: "topRight"
      });

      // Dispatch event to update entitlement status
      window.dispatchEvent(new CustomEvent("entitlementStatusChanged"));

      // Navigate to AI Import page
      history.push("/ai-import");
      onCancel();
    } catch (error) {
      console.error("Error activating AI Import:", error);
      notification.error({
        message: "Activation Failed",
        description:
          error.response?.data?.message ||
          "Failed to activate AI Import. Please try again.",
        placement: "topRight"
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format price with term (same logic as UpgradesPage)
  const formatPriceWithTerm = (cost, term) => {
    if (!cost || cost === 0) return "Free";

    // Determine term suffix based on term value
    let termSuffix = "";

    // Handle null/undefined terms - default to /month
    if (term === null || term === undefined) {
      console.warn("Term is null/undefined, defaulting to /month");
      termSuffix = " /month";
    } else {
      // Parse term as number if it's a string
      const numericTerm = typeof term === "string" ? parseInt(term, 10) : term;

      if (numericTerm === 0) {
        termSuffix = " /ea";
      } else if (numericTerm === 1) {
        termSuffix = " /day";
      } else if (numericTerm === 7) {
        termSuffix = " /week";
      } else if (numericTerm === 30) {
        termSuffix = " /month";
      } else if (numericTerm === 365) {
        termSuffix = " /year";
      } else {
        // If term doesn't match known values, default to /month
        console.warn(`Unknown term value: ${numericTerm}, defaulting to /month`);
        termSuffix = " /month";
      }
    }

    const numericCost = parseFloat(cost);

    // If amount is less than $1 and has sub-penny precision, show as cents
    if (numericCost < 1) {
      const cents = numericCost * 100;
      // Remove trailing zeros for cents display
      const centsFormatted =
        cents % 1 === 0 ? cents.toFixed(0) : cents.toFixed(1).replace(/\.?0+$/, "");
      return `${centsFormatted}¢${termSuffix}`;
    }

    // For dollar amounts, check if there are cents
    if (numericCost % 1 === 0) {
      // No cents, show as whole dollars
      return `$${numericCost.toFixed(0)}${termSuffix}`;
    } else {
      // Has cents, show full amount
      return `$${numericCost.toFixed(2)}${termSuffix}`;
    }
  };

  const formatPrice = () => {
    if (loadingPrice) return "Loading...";

    // Use the same fields as UpgradesPage: 'cost' and 'term' from entitlement
    if (!pricing || !pricing.cost) return "Contact us for pricing";

    console.log("Formatting price with:", {
      cost: pricing.cost,
      term: pricing.term,
      termType: typeof pricing.term
    });

    return formatPriceWithTerm(pricing.cost, pricing.term);
  };

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      footer={null}
      width="85%"
      centered
      className="ai-import-upsell-modal"
      style={{ maxWidth: "1280px" }}
    >
      <div className="upsell-content">
        <div className="upsell-icon">
          <RocketOutlined />
        </div>

        <h2 className="upsell-title">Want to supercharge your import process?</h2>
        <p className="upsell-subtitle">Enable AI-powered import now!</p>

        <div className="upsell-benefits">
          <div className="benefit-item">
            <CheckOutlined className="benefit-icon" />
            <div className="benefit-text">
              <strong>Save Hours of Manual Work</strong>
              <p>Transform any document format into optimized routes instantly</p>
            </div>
          </div>
          <div className="benefit-item">
            <CheckOutlined className="benefit-icon" />
            <div className="benefit-text">
              <strong>Unlimited Usage</strong>
              <p>Process as many routes as you need for one low monthly cost</p>
            </div>
          </div>
          <div className="benefit-item">
            <CheckOutlined className="benefit-icon" />
            <div className="benefit-text">
              <strong>Cutting-Edge AI Technology</strong>
              <p>Powered by advanced machine learning for unmatched accuracy</p>
            </div>
          </div>
        </div>

        <div className="upsell-pricing">
          <div className="price-amount">{formatPrice()}</div>
        </div>

        <div className="upsell-actions">
          <Button
            type="primary"
            size="large"
            className="lets-go-button"
            onClick={handleActivate}
            loading={loading}
            disabled={loadingPrice}
          >
            Let&apos;s Go!
          </Button>

          <button className="no-thanks-link" onClick={onCancel} disabled={loading}>
            No thanks, I&apos;ll do it the hard way
          </button>

          <p className="terms-text">
            By clicking Let&apos;s Go button, you are agreeing to the charges
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default AIImportUpsellModal;
